import moment, { Duration, Moment } from 'moment';

const months = {
    short: [
        'янв',
        'фев',
        'мар',
        'апр',
        'мая',
        'июн',
        'июл',
        'авг',
        'сен',
        'окт',
        'ноя',
        'дек',
    ],
    full: [
        'января',
        'февраля',
        'марта',
        'апреля',
        'мая',
        'июня',
        'июля',
        'августа',
        'сентября',
        'октября',
        'ноября',
        'декабря',
    ],
};

export type DateTimeInterval = {
    dateFrom: Moment;
    dateTo: Moment;
};

type IntervalGetterParams = {
    dateFrom: Moment;
    dateTo: Moment;
    duration: Duration;
    useRoundDates?: boolean;
};
function getIntervals(params: IntervalGetterParams) {
    const { dateFrom, dateTo } = params;
    const result: DateTimeInterval[] = [];

    let curDateTo = dateFrom.clone();
    let curDateFrom = dateFrom.clone();

    while (true) {
        curDateTo = getNextDateTo({ ...params, dateFrom: curDateFrom });

        result.push({
            dateFrom: curDateFrom,
            dateTo: curDateTo,
        });

        if (curDateTo.isSame(dateTo)) {
            break;
        }

        curDateFrom = curDateTo.clone().add(1, 'millisecond');
    }

    return result;
}

function getNextDateTo({
    dateFrom,
    dateTo,
    duration,
    useRoundDates,
}: IntervalGetterParams) {
    const units: moment.unitOfTime.Base[] = ['year', 'month', 'day'];
    const unit = units.find((unit) => duration.get(unit) === 1);

    let nextDateTo = dateFrom
        .clone()
        .add(duration)
        .subtract(1, 'milliseconds');

    if (useRoundDates && unit) {
        nextDateTo = dateFrom.clone().endOf(unit);
    }

    return moment.min(nextDateTo, dateTo);
}

function getMinDuration(...durations: Duration[]) {
    const durationsAsMs = durations.map((dur) => dur.asMilliseconds());
    const smallestInMs = Math.min(...durationsAsMs);
    const index = durationsAsMs.indexOf(smallestInMs);
    return durations[index];
}

/**
 * @namespace
 */
export const DateTimeUtils = {
    /**
     * @param {Date|number} [year] – full year or instance of Date
     * @returns {Boolean}
     */
    isLeapYear(date) {
        let year = date;

        if (!date) {
            date = new Date();
        }

        if (date instanceof Date) {
            year = date.getFullYear();
        }

        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    },

    /**
     * @param {Date|number} [date] – timestamp or instance of Date
     * @returns {number}
     */
    getMonthLength(date) {
        if (!date) {
            date = new Date();
        }

        if (typeof date == 'number') {
            date = new Date(date);
        }

        const month = date.getMonth();

        if (month === 1 && DateTimeUtils.isLeapYear(date)) {
            return 29;
        }

        return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
    },

    /**
     * @param {Date|number} date – timestamp or instance of Date
     * @param {string} pattern – like "hh:mm:ss.SSS dd.MM.yyyy (z)"
     * @returns {string}
     */
    // eslint-disable-next-line
    format(date, pattern) {
        if (typeof date == 'number') {
            date = new Date(date);
        }

        return pattern.match(/[hmsSdMyz]+/g).reduce((result, pattern) => {
            let value;

            switch (pattern.charAt(0)) {
                case 'h':
                    value = date.getHours();

                    break;

                case 'm':
                    value = date.getMinutes();

                    break;

                case 's':
                    value = date.getSeconds();

                    break;

                case 'S':
                    value = date.getMilliseconds();

                    break;

                case 'd':
                    value = date.getDate();

                    break;

                case 'M': {
                    const month = date.getMonth();

                    if (pattern.length > 2) {
                        if (pattern.length === 3) {
                            value = months.short[month];
                        } else {
                            value = months.full[month];
                        }
                    } else {
                        value = month + 1;
                    }

                    break;
                }
                case 'y':
                    if (pattern.length === 4) {
                        value = date.getFullYear();
                    } else {
                        value = date.getYear();

                        if (value > 100) {
                            value -= 100;
                        }
                    }

                    break;

                case 'z':
                    value = date.getTimezoneOffset() / 60;

                    break;
            }

            value = String(value);

            if (pattern.length > value.length) {
                value = '0000'.slice(0, pattern.length - value.length) + value;
            }

            return result.replace(pattern, value);
        }, pattern);
    },

    getIntervals,
    getMinDuration,
};

export default DateTimeUtils;
